import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Search, X, GripVertical } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import BotonAyuda from '../components/BotonAyuda';

export default function ProductosCategorizar({ embebido = false } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [soloSinCategoria, setSoloSinCategoria] = useState(true);
  const [categoriaActivaId, setCategoriaActivaId] = useState(null);
  const [arrastrandoId, setArrastrandoId] = useState(null);
  const [error, setError] = useState('');

  async function cargar() {
    setCargando(true);
    const [prod, cat] = await Promise.all([api.get('/productos'), api.get('/categorias')]);
    setProductos(prod.data);
    setCategorias(cat.data);
    setCargando(false);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  const categoriasEgreso = categorias.filter((c) => c.tipo === 'egreso');
  const categoriasPrincipales = categoriasEgreso.filter((c) => !c.padre_id);
  const subCategoriasDe = (padreId) => categoriasEgreso.filter((c) => String(c.padre_id) === String(padreId));

  // Los productos "solo venta" no se compran (no pasan por factura de
  // proveedor) ni tienen sentido de asociar a una cuenta contable acá:
  // las ventas entran al banco agrupadas como facturación, no producto
  // por producto. Los "contable" (ej. Gestión Administrativa, Servicio
  // de Extrusión) tampoco son inventario real, se excluyen igual. Los
  // de "ambas" (ej. Coca-Cola: se compra y se revende) sí se compran,
  // así que quedan incluidos junto a los de "solo compra".
  const productosCompra = useMemo(
    () => productos.filter((p) => p.disponibilidad === 'compra' || p.disponibilidad === 'ambas'),
    [productos]
  );

  const sinCategoria = useMemo(() => productosCompra.filter((p) => !p.categoria_id), [productosCompra]);

  const q = busqueda.trim().toLowerCase();
  const productosIzquierda = productosCompra
    .filter((p) => (soloSinCategoria ? !p.categoria_id : true))
    .filter((p) => !q || p.nombre.toLowerCase().includes(q))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  const productosDeCategoriaActiva = categoriaActivaId
    ? productosCompra.filter((p) => String(p.categoria_id) === String(categoriaActivaId)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
    : [];

  const categoriaActiva = categoriasEgreso.find((c) => c.id === categoriaActivaId);

  async function asignar(productoId, categoriaId) {
    setError('');
    const anterior = productos;
    setProductos((prev) => prev.map((p) => (p.id === productoId ? { ...p, categoria_id: categoriaId, categoria_nombre: categoriaActiva?.nombre ?? p.categoria_nombre } : p)));
    try {
      await api.put(`/productos/${productoId}`, { categoria_id: categoriaId });
    } catch (err) {
      setProductos(anterior);
      setError(err.response?.data?.error || 'No se pudo asignar la categoría');
    }
  }

  function onDrop(e) {
    e.preventDefault();
    const productoId = Number(e.dataTransfer.getData('text/plain'));
    setArrastrandoId(null);
    if (!productoId) return;
    if (!categoriaActivaId) {
      setError('Seleccioná una cuenta contable a la derecha antes de soltar el producto.');
      return;
    }
    asignar(productoId, categoriaActivaId);
  }

  if (cargando) return null;

  return (
    <div className="space-y-6">
      {!embebido && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-slate-800">
            Categorizar Productos
            {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
            <BotonAyuda />
          </h1>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-2">
          {sinCategoria.length > 0 ? (
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
          ) : (
            <CheckCircle2 size={16} className="text-teal-600 flex-shrink-0" />
          )}
          <p className="font-medium text-slate-600">
            {sinCategoria.length > 0
              ? `${sinCategoria.length} producto${sinCategoria.length === 1 ? '' : 's'} sin categoría contable asignada`
              : 'Todos los productos tienen categoría contable asignada'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr] gap-4">
        {/* Columna izquierda: productos */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col h-[560px]">
          <h2 className="font-semibold text-slate-700 mb-3">Productos</h2>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar producto..."
              className="border rounded-md pl-8 pr-2.5 py-1.5 w-full text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500 mb-3 select-none">
            <input
              type="checkbox"
              checked={soloSinCategoria}
              onChange={(e) => setSoloSinCategoria(e.target.checked)}
            />
            Solo sin categoría
          </label>
          <div className="flex-1 overflow-y-auto space-y-1">
            {productosIzquierda.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-6">
                {soloSinCategoria ? 'No hay productos sin categoría.' : 'Ningún producto coincide con la búsqueda.'}
              </p>
            )}
            {productosIzquierda.map((p) => (
              <div
                key={p.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(p.id));
                  setArrastrandoId(p.id);
                }}
                onDragEnd={() => setArrastrandoId(null)}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-grab active:cursor-grabbing transition-colors
                  ${arrastrandoId === p.id ? 'opacity-40' : 'hover:bg-slate-50'}`}
              >
                <GripVertical size={14} className="text-slate-300 flex-shrink-0" />
                <p className="min-w-0 flex-1 truncate text-slate-700">{p.nombre}</p>
                <div className="flex-shrink-0 flex flex-col items-end gap-0.5 max-w-[100px]">
                  {p.categoria_nombre && <span className="truncate text-[10px] text-slate-400 max-w-full">{p.categoria_nombre}</span>}
                  {p.disponibilidad === 'compra' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">Solo compra</span>
                  )}
                  {p.disponibilidad === 'ambas' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">Compra y venta</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Columna central: productos de la categoría activa (zona de drop) */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className={`bg-white rounded-xl shadow-sm p-4 flex flex-col h-[560px] border-2 border-dashed transition-colors
            ${categoriaActivaId ? 'border-primary-200' : 'border-transparent'}`}
        >
          <h2 className="font-semibold text-slate-700 mb-3">
            {categoriaActiva ? categoriaActiva.nombre : 'Arrastrá los productos aquí'}
          </h2>
          {!categoriaActivaId && (
            <p className="text-sm text-slate-400 text-center py-6">Seleccioná una cuenta contable a la derecha para empezar a asignar.</p>
          )}
          {categoriaActivaId && (
            <div className="flex-1 overflow-y-auto space-y-1">
              {productosDeCategoriaActiva.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">Todavía no hay productos en esta categoría. Arrastrá uno desde la izquierda.</p>
              )}
              {productosDeCategoriaActiva.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-sm">
                  <span className="truncate text-slate-700">{p.nombre}</span>
                  <button
                    onClick={() => asignar(p.id, null)}
                    title="Quitar categoría"
                    className="text-slate-400 hover:text-red-600 flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Columna derecha: cuentas contables */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col h-[560px]">
          <h2 className="font-semibold text-slate-700 mb-3">Cuentas contables</h2>
          <div className="flex-1 overflow-y-auto space-y-3">
            {categoriasPrincipales.length === 0 && (
              <p className="text-sm text-slate-400">No hay cuentas contables de egreso creadas todavía.</p>
            )}
            {categoriasPrincipales.map((padre) => (
              <div key={padre.id}>
                <button
                  onClick={() => setCategoriaActivaId(padre.id)}
                  className={`w-full text-left rounded-md px-2 py-1 text-sm font-medium transition-colors
                    ${categoriaActivaId === padre.id ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-50'}`}
                >
                  {padre.nombre}
                </button>
                <div className="ml-3 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
                  {subCategoriasDe(padre.id).map((hijo) => (
                    <button
                      key={hijo.id}
                      onClick={() => setCategoriaActivaId(hijo.id)}
                      className={`w-full text-left rounded-md px-2 py-1 text-sm transition-colors
                        ${categoriaActivaId === hijo.id ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      {hijo.nombre}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
